import type { ConceptId } from "../data/concepts";
import { BANK_ITEMS } from "../data/bank";
import { canonicalId } from "./analects";
import { gateForBankItem, type BankItem, type Difficulty } from "./bank-types";
import { conceptsOf } from "./conceptTag";
import { seededShuffle } from "./rng";
import type { Analect, Challenge, GateId } from "./types";
import { gateById } from "./gates";

/**
 * Selection over the authored bank.
 *
 * Two routes reach an item:
 *   direct  — the item names this passage in `refs`. Always preferred.
 *   concept — the item is about a concept this passage carries. This is how
 *             a hand-authored 致用 or 核心概念 item serves a passage nobody
 *             wrote a specific question for, which is what lets the authored
 *             bank cover all 512 passages instead of only the 150-odd it
 *             names directly.
 */

const byGateAndRef = new Map<string, BankItem[]>();
const byGateAndConcept = new Map<string, BankItem[]>();
const byId = new Map<string, BankItem>();

function push(map: Map<string, BankItem[]>, key: string, item: BankItem): void {
  const list = map.get(key);
  if (list) list.push(item);
  else map.set(key, [item]);
}

for (const item of BANK_ITEMS) {
  byId.set(item.id, item);
  const gate = gateForBankItem(item);
  for (const ref of new Set(item.refs.map(canonicalId))) {
    push(byGateAndRef, `${gate}:${ref}`, item);
  }
  for (const concept of new Set(item.concepts)) {
    push(byGateAndConcept, `${gate}:${concept}`, item);
  }
}

export function bankItemById(id: string): BankItem | undefined {
  return byId.get(id);
}

/** Items naming this passage directly, for the 本則 detail view. */
export function directItemsFor(analectId: number): BankItem[] {
  const id = canonicalId(analectId);
  return BANK_ITEMS.filter((item) => item.refs.map(canonicalId).includes(id));
}

export function bankCountFor(analectId: number, gate: GateId): number {
  return (byGateAndRef.get(`${gate}:${canonicalId(analectId)}`) ?? []).length;
}

/** Target difficulty band for a player at `mastery` (0..1) on this gate. */
function targetDifficulty(mastery: number): Difficulty {
  if (mastery < 0.34) return 1;
  if (mastery < 0.7) return 2;
  return 3;
}

export interface BankPick {
  item: BankItem;
  route: "direct" | "concept";
}

/**
 * Choose an authored item for (passage, gate). Prefer the nearest difficulty
 * band, then rotate through its items before repeating. A one-item band still
 * repeats; a new option order is not a new question.
 */
export function pickBankItem(
  analect: Analect,
  gate: GateId,
  opts: { variant?: number; mastery?: number; exclude?: Set<string> } = {},
): BankPick | null {
  const variant = opts.variant ?? 0;
  const exclude = opts.exclude ?? new Set<string>();
  const want = targetDifficulty(opts.mastery ?? 0);
  const id = canonicalId(analect.id);

  const direct = (byGateAndRef.get(`${gate}:${id}`) ?? []).filter((item) => !exclude.has(item.id));
  const viaConcept: BankItem[] = [];
  const seen = new Set(direct.map((item) => item.id));
  for (const concept of conceptsOf(analect) as ConceptId[]) {
    for (const item of byGateAndConcept.get(`${gate}:${concept}`) ?? []) {
      if (seen.has(item.id) || exclude.has(item.id)) continue;
      seen.add(item.id);
      viaConcept.push(item);
    }
  }

  const pickFrom = (pool: BankItem[]): BankItem | null => {
    if (!pool.length) return null;
    const distance = Math.min(...pool.map(item => Math.abs(item.difficulty - want)));
    const band = pool.filter(item => Math.abs(item.difficulty - want) === distance);
    const cycle = seededShuffle(band, (id * 131 + gate.length * 17 + want * 7919) >>> 0);
    return cycle[Math.max(0, Math.floor(variant)) % cycle.length] ?? null;
  };

  const directPick = pickFrom(direct);
  if (directPick) return { item: directPick, route: "direct" };
  const conceptPick = pickFrom(viaConcept);
  if (conceptPick) return { item: conceptPick, route: "concept" };
  return null;
}

/** Render an authored item as the Challenge the duel UI consumes. */
export function challengeFromBank(item: BankItem, seed: number, route: BankPick["route"] = "direct"): Challenge {
  const options = seededShuffle(
    item.options.map((option) => ({ id: option.id, text: option.text })),
    seed,
  );
  return {
    gate: gateForBankItem(item),
    prompt: gateById(gateForBankItem(item)).prompt,
    stem: item.stem,
    context: item.context,
    options,
    answerId: item.answerId,
    evidence: item.explanation,
    difficulty: item.difficulty / 3,
    source: "bank",
    route,
    bankId: item.id,
    questionReview: item.review,
    itemType: item.type,
    concepts: item.concepts,
    figures: item.figures,
    cognitive: item.cognitive,
    purpose: item.purpose,
    optionWhy: Object.fromEntries(
      item.options.filter((option) => option.why).map((option) => [option.id, option.why!]),
    ),
  };
}

export const BANK_SIZE = BANK_ITEMS.length;
